import classNames from 'classnames';
import {useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {
  Accordion,
  FiltersBar,
  ModalDialog,
  Paginator,
  XmarkButton,
} from '@macmillanplatform/ui-core';
import {useStateFromUrl} from '@macmillanplatform/ui-core/hooks';
import {ClickEvent, OnClickHandler} from '@macmillanplatform/ui-core/types';

import {
  ORDERS_DETAILS_URL_PARAMS,
  ORDERS_DETAILS_URL_PARAMS_PREFIX,
} from '../../constants';
import {CONFIGS_FIELDS} from '@/constants';
import {USER_FIELDS} from '@/features/auth/constants';

import {getStateFromUrl} from '../orders/helpers';
import {
  getFiltersFields,
  getLeftFiltersFields,
  getSections,
  handlePagination,
} from './helpers';
import {readAuthData, updateAuthData} from '@/features/auth/helpers';
import {useAppDispatch} from '@/hooks';
import {browserHistory} from '@/shared/utils';

import {
  clearOrderDetails,
  exportFile,
  fetchCustomersBillNames,
  fetchOrderDetails,
  fetchOrderDetailsCounts,
} from '../../actions';
import {
  selectCustomersBillName,
  selectCustomersBillNameLoading,
  selectOrderDetails,
  selectOrderDetailsCount,
  selectOrderDetailsCountsLoading,
  selectOrderDetailsLoading,
  selectOrderDetailsPoStatusCount,
  selectOrderDetailsShipmentsCount,
  selectOrderDetailsShippingStatusCount,
  selectOrderDetailsTitlesCount,
} from '../../reducer';
import {selectUserField} from '@/features/auth/slice';
import {selectConfigField} from '@/reducer';

import LabelValueList from './components/label-value-list';
import SummarySection from './components/summary-section';
import {PageContentWrapper} from '@/shared/components';

import './index.css';

interface OrderDetailsProps {
  poNumber: string;
  orderDate: string;
  closeHandler: OnClickHandler;
}

const OrderDetails = (props: OrderDetailsProps) => {
  const {poNumber, orderDate, closeHandler} = props;

  const dispatch = useAppDispatch();

  const accountNumber = useSelector(selectUserField(USER_FIELDS.LINK_ACCOUNT));
  const orderDetails = useSelector(selectOrderDetails);
  const orderDetailsLoading = useSelector(selectOrderDetailsLoading);
  const orderDetailsCount = useSelector(selectOrderDetailsCount);
  const orderDetailsCountsLoading = useSelector(
    selectOrderDetailsCountsLoading
  );
  const customersBillName = useSelector(selectCustomersBillName);
  const customersBillNameIsLoading = useSelector(
    selectCustomersBillNameLoading
  );
  const titlesCount = useSelector(selectOrderDetailsTitlesCount);
  const shipmentsCount = useSelector(selectOrderDetailsShipmentsCount);
  const shippingStatusCount = useSelector(
    selectOrderDetailsShippingStatusCount
  );
  const configShippingStatuses = useSelector(
    selectConfigField(CONFIGS_FIELDS.SHIPPING_STATUSES)
  );
  const configPoStatuses = useSelector(
    selectConfigField(CONFIGS_FIELDS.PO_STATUSES)
  );
  const poStatusCount = useSelector(selectOrderDetailsPoStatusCount);

  const [stateFromUrl] = useStateFromUrl(getStateFromUrl, browserHistory);
  const {searchStates, [ORDERS_DETAILS_URL_PARAMS.FILTERS]: filtersValues} =
    stateFromUrl;
  const {[ORDERS_DETAILS_URL_PARAMS_PREFIX]: searchPageOrderDetails} =
    searchStates;
  const {
    [ORDERS_DETAILS_URL_PARAMS.LIMIT]: limit,
    [ORDERS_DETAILS_URL_PARAMS.OFFSET]: offset,
  } = searchPageOrderDetails;
  const {
    [ORDERS_DETAILS_URL_PARAMS.SEARCH]: searchValue,
    [ORDERS_DETAILS_URL_PARAMS.SHIPPING_STATUS]: shippingStatuses,
    [ORDERS_DETAILS_URL_PARAMS.PO_STATUS]: poStatuses,
  } = filtersValues;

  const handleExportFile = useCallback(
    () => exportFile({po_number: poNumber, order_date: orderDate}),
    [poNumber, orderDate]
  );

  useEffect(() => {
    dispatch(
      fetchOrderDetails({
        po_number: poNumber,
        shipping_status: shippingStatuses,
        q: searchValue,
        order_date: orderDate,
        limit,
        offset,
        po_statuses: poStatuses,
      })
    );
  }, [
    dispatch,
    poNumber,
    shippingStatuses,
    searchValue,
    orderDate,
    limit,
    offset,
    poStatuses,
  ]);

  useEffect(() => {
    dispatch(
      fetchOrderDetailsCounts({
        po_number: poNumber,
        order_date: orderDate,
      })
    );
    dispatch(fetchCustomersBillNames());
  }, [poNumber, orderDate]);

  const orderInfoData = useMemo(
    () => [
      {label: 'Company Name', value: customersBillName},
      {label: 'Account Number', value: accountNumber},
      {label: 'PO #', value: poNumber},
    ],
    [customersBillName, accountNumber, poNumber]
  );

  const orderDateParts = orderDate.split('-');
  const formattedOrderDate =
    orderDateParts.length === 3
      ? `${orderDateParts[1].padStart(2, '0')}${orderDateParts[2].padStart(2, '0')}${orderDateParts[0]}`
      : orderDate;

  const filename = `${customersBillName}_${poNumber}_${formattedOrderDate}`;

  const sections = useMemo(() => getSections(orderDetails), [orderDetails]);
  const filtersFields = useMemo(
    () => getFiltersFields({exportFile: handleExportFile, filename}),
    [filename, handleExportFile]
  );
  const leftFiltersFields = useMemo(
    () =>
      getLeftFiltersFields({
        configPoStatuses,
        configShippingStatuses,
        selectedShippingStatus: shippingStatuses,
        shippingStatusCount,
        selectedPoStatuses: poStatuses,
        poStatusCount,
      }),
    [
      configPoStatuses,
      configShippingStatuses,
      shippingStatuses,
      shippingStatusCount,
      poStatuses,
      poStatusCount,
    ]
  );

  const handleClose = (event: ClickEvent) => {
    dispatch(clearOrderDetails());
    closeHandler(event);
  };

  const handleUpdateLimit = (pageLimit: number) =>
    updateAuthData({
      order_details: {...readAuthData()?.order_details, limit: pageLimit},
    });

  return (
    <ModalDialog
      show
      closeOnEsc
      onClose={handleClose}
      className="OrderDetails__modal"
    >
      <div
        className={classNames('OrderDetails__modal-content', {
          'OrderDetails__modal-content--limit-5': limit == 5,
          'OrderDetails__modal-content--limit-10': limit == 10,
          'OrderDetails__modal-content--limit-15': limit == 15,
          'OrderDetails__modal-content--limit-20': limit == 20,
        })}
      >
        <div className="OrderDetails__modal-header">
          <span className="OrderDetails__modal-title">
            Purchase Order Detail
          </span>
          <XmarkButton
            className="OrderDetails__title--close"
            onClick={handleClose}
          />
        </div>

        {!customersBillNameIsLoading && (
          <LabelValueList
            data={orderInfoData}
            className="OrderDetails__bill-name"
          />
        )}

        <div className="OrderDetails__modal-divider" />

        {!orderDetailsCountsLoading && (
          <>
            <SummarySection
              titlesCount={titlesCount}
              shipmentsCount={shipmentsCount}
            />
            <FiltersBar
              filtersValues={filtersValues}
              filtersFields={filtersFields}
              leftFiltersFields={leftFiltersFields}
            />
          </>
        )}

        <PageContentWrapper
          className="OrderDetails__page-wrapper"
          loading={orderDetailsLoading || orderDetailsCountsLoading}
          hasResults={!!orderDetailsCount}
          emptyStateTitle="No order details found!"
        >
          <Accordion className="OrderDetails__accordion" sections={sections} />

          <Paginator
            rowsPerPage={[5, 10, 15, 20]}
            initialOffset={offset}
            count={orderDetailsCount}
            limit={limit}
            onPageSelect={handlePagination(ORDERS_DETAILS_URL_PARAMS_PREFIX)}
            className="OrderDetails__paginator"
            onRowsPerPageChange={handleUpdateLimit}
          />
        </PageContentWrapper>
      </div>
    </ModalDialog>
  );
};

export default OrderDetails;
